import { useEffect, useRef } from 'react';

export type ShortcutHandler = (e: KeyboardEvent) => void;

/**
 * Shape of a single registered shortcut.
 *
 *   keys: a space-separated sequence ("g s", "?", "b", "r").
 *          - 1 token  → single-key match (e.g. "?" fires on `?`)
 *          - 2 tokens → chord; second key must follow the first within
 *            `chordTtlMs` (default 1200ms)
 *   description / group are surface-only — used by the cheatsheet modal.
 *   global=true bypasses the input-focus guard (rarely needed; the palette
 *          uses this for Escape).
 */
export interface ShortcutSpec {
  keys: string;
  description: string;
  group: string;
  handler: ShortcutHandler;
  global?: boolean;
}

interface RegistryEntry extends ShortcutSpec {
  /** Pre-split tokens for matching. */
  tokens: string[];
}

/** Module-scoped registry — one global listener serves all hooks. */
const registry = new Set<RegistryEntry>();
let listenerAttached = false;

/** Buffer of recently-pressed tokens, joined with single spaces. */
let buffer = '';
let bufferTimer: number | undefined;
const CHORD_TTL_MS = 1200;

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (t.isContentEditable) return true;
  // AntD inputs render the textbox role on a sibling for some components.
  if (t.getAttribute('role') === 'textbox') return true;
  return false;
}

/**
 * Normalize a keydown event to a single token. We intentionally drop modifier
 * combinations because the only modifier shortcut in the app (`⌘K`) is wired
 * directly in <AppLayout>; everything else is plain-key navigation.
 */
function tokenFromEvent(e: KeyboardEvent): string | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const k = e.key;
  if (k.length === 1) return k.toLowerCase();
  if (k === 'Escape') return 'Escape';
  if (k === 'Enter') return 'Enter';
  // Other named keys (Arrow*, Tab, Backspace, etc.) aren't shortcut targets
  // here — drop them so they don't pollute the buffer.
  return null;
}

function resetBuffer() {
  buffer = '';
  if (bufferTimer != null) {
    clearTimeout(bufferTimer);
    bufferTimer = undefined;
  }
}

function onKeydown(e: KeyboardEvent) {
  const tok = tokenFromEvent(e);
  if (tok == null) return;

  // Build a candidate buffer — keep at most the last two tokens.
  const next = (buffer ? buffer + ' ' : '') + tok;
  const lastTwo = next.split(' ').slice(-2).join(' ');

  const inEditable = isEditableTarget(e.target);

  // Prefer the longest match: try the 2-key chord first, then the single key.
  // This lets "g r" win over "r" when the user typed "g" then "r".
  for (const want of [lastTwo, tok]) {
    for (const s of registry) {
      if (s.tokens.length === 1 && want !== s.tokens[0]) continue;
      if (s.tokens.length === 2 && want !== s.tokens.join(' ')) continue;
      if (s.tokens.length !== want.split(' ').length) continue;
      if (inEditable && !s.global) continue;
      e.preventDefault();
      resetBuffer();
      s.handler(e);
      return;
    }
  }

  // No match — record the token in the buffer and arm the TTL so chords can
  // still complete on the next press.
  buffer = lastTwo;
  if (bufferTimer != null) clearTimeout(bufferTimer);
  bufferTimer = window.setTimeout(resetBuffer, CHORD_TTL_MS);
}

function ensureListener() {
  if (listenerAttached) return;
  window.addEventListener('keydown', onKeydown);
  listenerAttached = true;
}

/**
 * Registers shortcuts for the lifetime of the calling component. The list is
 * passed via ref so a stable identity isn't required — re-renders don't trigger
 * re-registration, but the handlers always see the latest closure values.
 */
export function useShortcuts(shortcuts: ShortcutSpec[]) {
  // Keep the live handler closures in a ref so we don't churn the registry on
  // every parent re-render.
  const specsRef = useRef(shortcuts);
  specsRef.current = shortcuts;

  useEffect(() => {
    const entries: RegistryEntry[] = specsRef.current.map((s) => ({
      ...s,
      tokens: s.keys.trim().split(/\s+/),
      // Wrap so the entry always sees the latest closure via specsRef.
      handler: (e) => {
        const live = specsRef.current.find((x) => x.keys === s.keys);
        live?.handler(e);
      },
    }));
    for (const e of entries) registry.add(e);
    ensureListener();
    return () => {
      for (const e of entries) registry.delete(e);
      // We intentionally leave the listener attached — registry can grow/shrink
      // freely without flipping the listener on and off.
    };
    // We depend on stringified keys so a different shortcut set re-registers.
    // The handlers themselves can change without re-registering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcuts.map((s) => s.keys).join('|')]);
}

/** Read-only snapshot of currently registered shortcuts (for the cheatsheet). */
export function getRegisteredShortcuts(): ShortcutSpec[] {
  return Array.from(registry, (e) => ({
    keys: e.keys,
    description: e.description,
    group: e.group,
    handler: e.handler,
    global: e.global,
  }));
}
